import { useEffect, useState } from 'react';
import tw from 'twin.macro';

const HiddenInput = tw.input`opacity-0 w-0 h-0`;
const Container = tw.label`relative flex items-center text-xs cursor-pointer`;
const StyledSwitch = tw.span`w-[26px] h-3.5 flex items-center bg-gray-1 rounded-full p-[0.09rem] cursor-pointer overflow-hidden`;
const SwitchToggler = tw.span`bg-text-and-icon-invert h-3 w-3 rounded-full shadow-ui duration-300 ease-in-out m-0`;
const Label = tw.span`inline-block ml-2`;

export const Switch = ({
  id,
  name,
  label,
  onChange,
  onClick,
  isActive = false,
  height,
  width,
  disabled,
  title,
}: {
  id: string;
  name: string;
  onChange: (val: boolean) => void;
  onClick?: (val: boolean) => void;
  isActive: boolean;
  label?: string;
  height?: number;
  width?: number;
  disabled?: boolean;
  title?: string;
}): JSX.Element => {
  const [_isActive, _setIsActive] = useState(isActive);

  useEffect(() => {
    onChange(_isActive);
  }, [_isActive]);

  const handleClick = () => {
    onClick && onClick(!_isActive);
  };

  useEffect(() => _setIsActive(isActive), [isActive]);

  // Subtract 2 from height and width in order to have 1 pixel of margin on each side.
  const togglerSize = height ? height - 2 : 12;
  const transformPosition = width ? width / 2 - 2 : 11;

  return (
    <Container htmlFor={id} data-testid='switch-label-wrapper' title={title}>
      <HiddenInput
        id={id}
        name={name}
        type='checkbox'
        disabled={disabled}
        onChange={() => _setIsActive(!_isActive)}
        onClick={handleClick}
        data-testid='switch-input'
      />
      <StyledSwitch
        css={[
          _isActive && tw`bg-accent-2`,
          disabled && tw`cursor-not-allowed opacity-50 pointer-events-none`,
          { height, width },
        ]}>
        <SwitchToggler
          css={[
            {
              transform: _isActive ? `translateX(${transformPosition}px)` : '',
              height: togglerSize,
              width: togglerSize,
            },
          ]}
        />
      </StyledSwitch>
      {label ? <Label data-testid='switch-label-copy'>{label}</Label> : null}
    </Container>
  );
};
